import { Component, Input } from '@angular/core';
import { PAGINATION } from '../../../../core/constants/constants';
import { catchError, map, Observable, switchMap, tap } from 'rxjs';
import { Profile } from '../../../../core/models/users.interface';
import { Pagination } from '../../../../core/models/pagination.interface';
import { ProfileService } from '../../../../core/services/profile/profile';
import { LayoutService } from '../../../../core/services/layout/layout';
import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Title } from '@angular/platform-browser';
import { Filter, QueryGet } from '../../../../core/types/query-types';
import { ProfileForm } from '../profile-form/profile-form';
import { MatIcon } from '@angular/material/icon';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
} from '@angular/material/table';
import { NgxPaginationModule } from 'ngx-pagination';
import { AsyncPipe } from '@angular/common';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { OnlysuperuserDirective } from '../../../../shared/directives/onlysuperuser.directive';
import { PermisionsAssign } from '../../../components/permisions-assign/permisions-assign';
import { MatDividerModule } from '@angular/material/divider';
import { EditPassword } from '../../../components/edit-password/edit-password';
import { GroupAssign } from '../../../components/group-assign/group-assign';
import { LoadingService } from '../../../../core/services/loading/loading';
import { PermisionDirective } from '../../../../shared/directives/permision.directive';
import { Search } from '../../../../shared/components/search/search';
import { SectorAssing } from '../../../components/sector-assing/sector-assing';
import { TermPipe } from '../../../../shared/pipes/term.pipe';
import { TermKey } from '../../../../core/models/terminology.interface';

@Component({
  selector: 'app-profile-list',
  imports: [
    MatIcon,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    NgxPaginationModule,
    AsyncPipe,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    OnlysuperuserDirective,
    PermisionDirective,
    MatDividerModule,
    Search,
    TermPipe,
  ],
  templateUrl: './profile-list.html',
  styleUrl: './profile-list.css',
})
export class ProfileList {
  public page: number | undefined;
  @Input() filtro: string = '';
  @Input() orden: string = '';
  limit = 10;
  total = 0;
  profileList: Profile[] = [];
  asyncProfile!: Observable<Pagination<Profile[]>>;
  selectedProfile: Profile | null = null;
  termKey = TermKey;
  showColumns: string[] = [
    'acciones',
    'id',
    'usuario',
    'nombres',
    'email',
    'fechaRegistro',
    'permisos',
    'grupos',
    'sectores',
  ];

  constructor(
    private profileService: ProfileService,
    private layoutService: LayoutService,
    private toastr: ToastrService,
    private activatedRouter: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private title: Title,
    private loadingService: LoadingService,
  ) {
    this.title.setTitle('Perfiles');
  }

  ngOnInit(): void {
    this.layoutService.paginationLimit$
      .pipe(
        switchMap((limit) => {
          this.limit = limit;
          return this.activatedRouter.queryParamMap;
        }),
        switchMap((params) => {
          const pageNum = params.get('page') || '1';
          const search = params.get('search') || '';
          this.page = parseInt(pageNum);
          this.fetchProfile(PAGINATION.first_page, null, search);
          return this.layoutService.paginationLimit$;
        }),
      )
      .subscribe();
  }

  fetchProfile(page: number, filter: Filter | null = null, search = ''): void {
    const offset = page > 1 ? page * this.limit - this.limit : 0;
    const params: QueryGet = {
      order: '-id',
      limit: this.limit,
      offset,
      search,
      filters: [{ name: 'is_active', value: 1 }],
    };
    if (filter) {
      params.filters.push(filter);
    }
    this.page = page;
    this.loadingService.show(true);
    this.asyncProfile = this.profileService.allProfiles(params).pipe(
      tap((response) => {
        this.profileList = response.results;
        this.profileService.setProfiles(response.results);
        this.total = response.count;
      }),
      map((res) => {
        this.loadingService.show(false);
        return res;
      }),
      catchError((error) => {
        this.loadingService.show(false);
        const errorMsg = `¡Se ha producido un error, inténtelo nuevamente!`;
        this.toastr.error(errorMsg, 'Error!', {
          progressBar: true,
          timeOut: 5000,
        });
        this.router.navigate(['home']);
        return [];
      }),
    );
  }

  updatePassword(profile: Profile) {
    this.selectedProfile = profile;
    const dialogRef = this.dialog.open(EditPassword, {
      data: { selectedProfile: this.selectedProfile },
      width: '90%',
      maxWidth: '700px',
      minWidth: '320px',
      maxHeight: '90vh',
      autoFocus: false,
      disableClose: true,
      panelClass: 'custom-dialog',
    });
    dialogRef.afterClosed().subscribe(() => {
      this.fetchProfile(PAGINATION.first_page);
    });
  }

  createProfile() {
    this.profileService.removeProfile();
    const dialogRef = this.dialog.open(ProfileForm, {
      width: '90%',
      maxWidth: '700px',
      minWidth: '320px',
      maxHeight: '90vh',
      autoFocus: false,
      disableClose: true,
      panelClass: 'custom-dialog',
    });
    dialogRef.afterClosed().subscribe(() => {
      this.fetchProfile(PAGINATION.first_page);
    });
  }

  editar(profile: Profile) {
    this.profileService.setProfile(profile);
    const dialogRef = this.dialog.open(ProfileForm, {
      width: '90%',
      maxWidth: '700px',
      minWidth: '320px',
      maxHeight: '90vh',
      autoFocus: false,
      disableClose: true,
      panelClass: 'custom-dialog',
    });
    dialogRef.afterClosed().subscribe(() => {
      this.fetchProfile(PAGINATION.first_page);
    });
  }

  openDialog(profile: Profile, action: 'group' | 'user' | 'sector' = 'group') {
    if (action === 'group') {
      const dialogRef = this.dialog.open(GroupAssign, {
        width: 'auto',
        height: 'auto',
        maxHeight: '80vh',
        disableClose: true,
        data: { profile },
      });
      dialogRef.afterClosed().subscribe(() => {
        this.fetchProfile(PAGINATION.first_page);
      });
    }
    if (action === 'user') {
      const dialogRef = this.dialog.open(PermisionsAssign, {
        width: 'auto',
        height: 'auto',
        maxHeight: '80vh',
        disableClose: true,
        data: { profile },
      });
      dialogRef.afterClosed().subscribe(() => {
        this.fetchProfile(PAGINATION.first_page);
      });
    }
    if (action === 'sector') {
      const dialogRef = this.dialog.open(SectorAssing, {
        width: 'auto',
        height: 'auto',
        maxHeight: '80vh',
        disableClose: true,
        data: { profile },
      });
      dialogRef.afterClosed().subscribe(() => {
        this.fetchProfile(PAGINATION.first_page);
      });
    }
  }

  onRevokePermisions(profile: Profile) {
    const confirmMsg = `Esta operación revocará todos los accesos del usuario: ${profile.username}-${profile.first_name} ${profile.last_name}\n\n¿Desea continuar?`;
    const isConfirmed = confirm(confirmMsg);

    if (!isConfirmed) return;
    this.profileService.revokePermissions(profile.id).subscribe({
      next: () => {
        this.toastr.success(
          `Se revocaron todos los permisos del usuario ${profile.username}-${profile.first_name} ${profile.last_name}, correctamente!`,
          'Exitoso!',
        );
        this.fetchProfile(PAGINATION.first_page);
      },
      error: (err) => {
        this.toastr.error(
          `No pudimos revocar los permisos del usuario ${profile.username}-${profile.first_name} ${profile.last_name}`,
          'Error',
        );
      },
    });
  }

  onRevokeGroups(profile: Profile) {
    const confirmMsg = `Esta operacion revocará todos los grupos al usuario ${profile.username} ¿Estas seguro?`;
    const isConfirmed = confirm(confirmMsg);

    if (!isConfirmed) return;
    this.profileService.revokeGroups(profile.id).subscribe({
      next: () => {
        this.toastr.success(
          `Se revocaron todos los grupos del usuario ${profile.username}-${profile.first_name} ${profile.last_name}, correctamente!`,
          'Exitoso!',
        );
        this.fetchProfile(PAGINATION.first_page);
      },
      error: (err) => {
        this.toastr.error(
          `No se pudimos revocar los grupos del usuario ${profile.username}-${profile.first_name} ${profile.last_name}`,
          'Error',
        );
      },
    });
  }

  onDelete(id: number) {
    const confirmMsg = `¿Está seguro de eliminar este usuario?`;
    const isConfirmed = confirm(confirmMsg);

    if (!isConfirmed) return;
    this.profileService.deleteProfile(id).subscribe({
      next: () => {
        this.toastr.success(`Usuario eliminado correctamente!`, 'Exitoso!');
        this.fetchProfile(PAGINATION.first_page);
      },
      error: (err) => {
        this.toastr.error(`No se pudimos eliminar el usuario`, 'Error');
      },
    });
  }
}
